import type { IntlShape } from 'react-intl'
import type { FlightLeg } from '../../domain/types'
import type { Locale } from '@/intl/types'
import { airlineCodesMap, type AirlineIconGlyph } from '@sunwebgroup/uds-ui/airline-icon'
import { differenceInMinutes, isValid, parseISO } from 'date-fns'

const FLIGHT_STOPOVER_DURATION_TRANSLATION_KEY = {
  minutes: 'booking_details_flight_stopover_minutes',
  hoursMinutes: 'booking_details_flight_stopover_hours_minutes',
}

export function getFlightStopsCount(legs: FlightLeg[]): number {
  return Math.max(0, legs.length - 1)
}

export function hasMixedFlightStops(directionsLegs: (FlightLeg[] | null)[]): boolean {
  const stopsCounts = directionsLegs.filter((legs): legs is FlightLeg[] => Boolean(legs)).map(getFlightStopsCount)
  return stopsCounts.some(count => count > 0) && stopsCounts.includes(0)
}

export function getFlightTitle(legs: FlightLeg[]): string {
  if (!legs.length) {
    return ''
  }

  const firstLeg = legs[0]
  const lastLeg = legs[legs.length - 1]
  return `${firstLeg.departureAirportName} - ${lastLeg.arrivalAirportName}`
}

export function formatFlightTime(dateTime: string, locale: Locale = 'nl-NL', isKnownFlightTime: boolean): string {
  const unknownFlightTime = '--:--'

  if (!isKnownFlightTime) {
    return unknownFlightTime
  }

  const normalizedDateTime = dateTime.trim()
  if (!normalizedDateTime) {
    return unknownFlightTime
  }

  const date = parseISO(normalizedDateTime)
  if (!isValid(date)) {
    return unknownFlightTime
  }

  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function formatAirportLabel(code: string, name: string): string {
  return `${code} - ${name}`
}

export function getAirlineIconGlyph(airlineCode: string): AirlineIconGlyph | undefined {
  return airlineCodesMap[airlineCode]
}

export function getStopoverDurationMinutes(previousLeg: FlightLeg, nextLeg: FlightLeg): number | null {
  const arrivalDate = parseISO(previousLeg.arrivalDateTime)
  const departureDate = parseISO(nextLeg.departureDateTime)

  if (!isValid(arrivalDate) || !isValid(departureDate)) {
    return null
  }

  const durationMinutes = differenceInMinutes(departureDate, arrivalDate)

  if (durationMinutes < 0) {
    return null
  }

  return durationMinutes
}

export function formatStopoverDuration(totalMinutes: number, formatMessage: IntlShape['formatMessage']): string {
  if (totalMinutes <= 60) {
    return formatMessage(
      { id: FLIGHT_STOPOVER_DURATION_TRANSLATION_KEY.minutes },
      { minutes: totalMinutes },
    )
  }

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return formatMessage(
    { id: FLIGHT_STOPOVER_DURATION_TRANSLATION_KEY.hoursMinutes },
    { hours, minutes },
  )
}
